import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import FormInputTextField from './FormInputTextField';
import { FormInputDatePickerV2 } from './FormInputDatePickerV2';
import { Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { useSelector } from 'react-redux';
import { candidateEmployementSelection } from '@AMIEWEB/Candidate/CandidateProfile/Profile/CandidateDetails.selector';
import { isWithinInterval, isAfter, isBefore } from 'date-fns';
import { FormCheckbox } from './FormCheckbox';
import { useEditTimeOffStyles } from './EditTimeOff.styles';

export interface IEditTimeOffForm {
  start: any;
  end: any;
  isOnGoing: boolean;
  reason: string;
}

export interface EditTimeOffProps {
  setSaveDisabled: (value: boolean) => void;
  workHistoryId: number;
}

export const EditTimeOffForm = (props: EditTimeOffProps) => {
  const { classes } = useEditTimeOffStyles();
  const candidateEmployment = useSelector(candidateEmployementSelection);

  const { watch, setValue, setError, clearErrors } = useFormContext();
  const [isEndDateRequired, setIsEndDateRequired] = useState(watch('isOnGoing'));
  const [errorMessageOpen, setErrorMessageOpen] = useState<boolean>(false);
  const watchedStart = watch('start');
  const watchedEnd = watch('end');
  const watchedIsOnGoing = watch('isOnGoing');

  useEffect(() => {
    setIsEndDateRequired(!watch('isOnGoing'));
    if (watch('isOnGoing')) {
      setValue('end', null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedIsOnGoing]);

  const yesAction = () => {
    setErrorMessageOpen(false);
    props.setSaveDisabled(false);
  };

  const isOverlappingDates = (startDate: Date, endDate: Date) => {
    let isOverlapping = false;
    candidateEmployment
      ?.filter(e => e.workHistoryId != props.workHistoryId)
      ?.forEach(item => {
        if (item.endedOn > item.startedOn) {
          if (
            item.endedOn &&
            (isWithinInterval(startDate, {
              start: new Date(item.startedOn),
              end: item.endedOn ? new Date(item.endedOn) : null,
            }) ||
              isWithinInterval(endDate, {
                start: new Date(item.startedOn),
                end: item.endedOn ? new Date(item.endedOn) : null,
              }))
          ) {
            isOverlapping = true;
          } else if (
            !item.endedOn &&
            (isAfter(startDate, new Date(item.startedOn)) || isAfter(endDate, new Date(item.startedOn)))
          ) {
            isOverlapping = true;
          } else if (
            item.endedOn &&
            isBefore(startDate, new Date(item.startedOn)) &&
            isAfter(endDate, new Date(item.endedOn))
          ) {
            isOverlapping = true;
          }
        }
      });
    return isOverlapping;
  };

  useEffect(() => {
    const endFromDate = watchedEnd ? new Date(watchedEnd) : null;
    const startDate = watchedStart ? new Date(watchedStart) : null;

    if (startDate && endFromDate && isOverlappingDates(startDate, endFromDate)) {
      setErrorMessageOpen(true);
      props.setSaveDisabled(true);
    } else {
      setErrorMessageOpen(false);
      props.setSaveDisabled(false);
    }
    if (!watch('onGoing') && endFromDate?.getTime() < startDate?.getTime()) {
      setError('end', {
        type: 'validate',
        message: 'End date must be greater than start date',
      });
    } else {
      clearErrors('end');
    }
    if (startDate?.getTime() > endFromDate?.getTime()) {
      setError('start', {
        type: 'validate',
        message: 'Start date must be less than end date',
      });
    } else {
      clearErrors('start');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch('start'), watch('end')]);

  return (
    <>
      {errorMessageOpen && (
        <LayoutGridItem style={{ height: '100px' }}>
          <LayoutGrid
            container
            direction="row"
            className={classes.root}
            spacing={2}
            style={{ backgroundColor: '#FDEDE9', padding: '10px', paddingBottom: '15px' }}
            alignItems={'center'}
            justifyContent={'space-between'}
            xs={12}
          >
            <LayoutGridItem>
              <LayoutGrid container direction="row" className={classes.root} spacing={2}>
                <LayoutGridItem>
                  <ErrorOutlineIcon style={{ color: '#F4574A' }} />
                </LayoutGridItem>
                <LayoutGridItem>
                  <LayoutGrid container direction="column" className={classes.root} spacing={0}>
                    <LayoutGridItem>
                      <span style={{ color: '#611A16', fontSize: '14px', fontWeight: 400 }}>
                        {'Dates are overlapping with existing work experience. This will cause a delay in submission,'}
                      </span>
                    </LayoutGridItem>
                    <LayoutGridItem>
                      <span style={{ color: '#611A16', fontSize: '14px', fontWeight: 400 }}>
                        {'do you wish to proceed?'}
                      </span>
                    </LayoutGridItem>
                  </LayoutGrid>
                </LayoutGridItem>
              </LayoutGrid>
            </LayoutGridItem>
            <LayoutGridItem>
              <LayoutGrid
                container
                direction="row"
                className={classes.root}
                spacing={2}
                style={{ paddingRight: '15px' }}
              >
                <LayoutGridItem>
                  <span className={classes.buttonStyle} onClick={() => setErrorMessageOpen(false)}>
                    {'No'}
                  </span>
                </LayoutGridItem>
                <LayoutGridItem>
                  <span className={classes.buttonStyle} onClick={() => yesAction()}>
                    {'Yes'}
                  </span>
                </LayoutGridItem>
              </LayoutGrid>
            </LayoutGridItem>
          </LayoutGrid>
        </LayoutGridItem>
      )}
      <LayoutGrid container direction="column" className={classes.root} spacing={4} style={{ width: '870px' }}>
        <LayoutGridItem justifyItems="center" style={{ marginTop: '24px' }}>
          <Typography component="span">Time Off</Typography>
        </LayoutGridItem>
        <LayoutGridItem item>
          <LayoutGrid
            container
            classes={{ root: classes.root }}
            direction="row"
            data-testid="container"
            justifyContent="flex-start"
            alignItems={'center'}
            xs={12}
          >
            <LayoutGridItem item xs={4}>
              <FormInputDatePickerV2 name={'start'} label={'Start Date*'} required={true}></FormInputDatePickerV2>
            </LayoutGridItem>
            <LayoutGridItem item xs={4}>
              <FormInputDatePickerV2
                name="end"
                label={isEndDateRequired ? 'End Date*' : 'End Date'}
                required={isEndDateRequired}
              ></FormInputDatePickerV2>
            </LayoutGridItem>
            <LayoutGridItem item xs={4}>
              <FormCheckbox name="isOnGoing" label="Ongoing" defaultChecked={watch('isOnGoing')}></FormCheckbox>
            </LayoutGridItem>
            <LayoutGridItem xs={6}>
              <FormInputTextField name="reason" label={'Reason for Leaving'} />
            </LayoutGridItem>
          </LayoutGrid>
        </LayoutGridItem>
      </LayoutGrid>
    </>
  );
};
